<script lang="ts">
	import { get } from 'svelte/store';
	import { format } from 'svelte-i18n';
	import { Loader, Text } from '@Shelly/shelly-design-system';
	import { devices, user_settings } from 'src/stores';
	import LayerChartRenderer from 'src/components/Chart/LayerChart/LayerChartRenderer.svelte';
	import { fetchDeviceEnergyStatistics } from '../helpers/fetch_device_energy_statistics';
	import type { ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';
	import type { EnergyMonitoringPeriodId } from '../helpers/period_helpers';
	import type { ChartDataInterface, ChartStatisticInterface } from 'src/types/Chart';
	import type { Device } from 'src/types/Device';
	import { estimateChartConsumption } from 'src/helpers/statistics/core/consumptions/consumption_helpers';
	import EmWidgetShell from '../EmWidgetShell.svelte';
	import EmWidgetState from '../EmWidgetState.svelte';
	import EmLayerChartFrame from '../EmLayerChartFrame.svelte';
	import EmChartLegend from '../EmChartLegend.svelte';
	import { energyMonitoringDataMode } from '../helpers/store';
	import { buildDemoEnergyMonitoringStats } from '../helpers/energy_demo_providers';
	import { buildGenerationLegend, toDsChartData } from '../helpers/layer_chart_adapter';
	import {
		ENERGY_ADD_DEVICE_HREF,
		hasPowerMeterDevices,
	} from '../helpers/energy_widget_state_helpers';

	const DEVICE_CONSUMPTION_SERIES = new Set(['consumption', 'total_house_energy']);
	const CHART_FRAME_HEIGHT = 196;
	const DEVICE_TREND_CHART_COLOR = '#248def';
	const DEVICE_TREND_COLOR_SCHEME = [DEVICE_TREND_CHART_COLOR];

	export let deviceId: string | undefined;
	export let fallbackDeviceIdsKey = '';
	export let controls: ChartControlsDataInterface;
	export let periodId: EnergyMonitoringPeriodId;
	export let staticPreview = false;

	let loading = false;
	let loadError = false;
	let chartData: ChartDataInterface | null = null;
	let chartStatistic: ChartStatisticInterface | null = null;
	let displayedDevice: Device | undefined;
	let token = 0;

	$: device = deviceId ? $devices[deviceId] : undefined;
	$: deviceName =
		displayedDevice?.name ?? device?.name ?? $format('label_device', { default: 'Device' });
	$: useDemoData = staticPreview || $energyMonitoringDataMode === 'demo';
	$: normalizedChartData = toDsChartData(chartData);
	$: displayChartData = normalizedChartData
		? {
				...normalizedChartData,
				type: normalizedChartData.type === 'pie' ? 'pie' : ('area' as const),
				chartLayout: 'dashboard-widget' as const,
				height: CHART_FRAME_HEIGHT,
			}
		: null;
	$: legendItems = buildGenerationLegend(normalizedChartData, (key, fallback) =>
		$format(key, { default: fallback }),
	).map((item) => ({
		...item,
		label: DEVICE_CONSUMPTION_SERIES.has(item.id) ? deviceName : item.label,
		color: DEVICE_CONSUMPTION_SERIES.has(item.id) ? DEVICE_TREND_CHART_COLOR : item.color,
	}));
	$: accentColor = DEVICE_TREND_CHART_COLOR;
	$: statsDetail =
		chartStatistic?.min && chartStatistic?.max && chartStatistic?.average
			? $format('energy_device_trend_stats_detail', {
					default: 'L: {min} · H: {max} · Avg: {avg}',
					values: {
						min: chartStatistic.min,
						max: chartStatistic.max,
						avg: chartStatistic.average,
					},
				})
			: '';

	const resolveStatistic = (
		nextChartData: ChartDataInterface | null,
		nextDevice: typeof device,
		explicit: ChartStatisticInterface | null | undefined,
	): ChartStatisticInterface | null => {
		try {
			if (explicit?.available) return explicit;
			if (!nextChartData) return null;
			return estimateChartConsumption(
				nextChartData,
				nextDevice?.type ?? '',
				get(user_settings)?.currency ?? '',
			);
		} catch {
			return explicit ?? null;
		}
	};

	const load = async (
		nextDevice: typeof device,
		nextControls: ChartControlsDataInterface,
		nextPeriodId: EnergyMonitoringPeriodId,
		demo: boolean,
		candidateIdsKey: string,
	) => {
		const current = ++token;
		loadError = false;
		if (demo) {
			const demoChart = buildDemoEnergyMonitoringStats().chartData;
			chartData = demoChart;
			chartStatistic = resolveStatistic(demoChart, nextDevice, null);
			displayedDevice = nextDevice;
			loading = false;
			return;
		}
		if (!nextDevice) {
			chartData = null;
			chartStatistic = null;
			displayedDevice = undefined;
			loading = false;
			return;
		}
		loading = true;
		try {
			const candidateIds = [nextDevice.id, ...candidateIdsKey.split(',').filter(Boolean)].filter(
				(id, index, all) => all.indexOf(id) === index,
			);
			let result: Awaited<ReturnType<typeof fetchDeviceEnergyStatistics>> = null;
			let resultDevice = nextDevice;
			for (const candidateId of candidateIds) {
				const candidate = get(devices)[candidateId];
				if (!candidate) continue;
				const candidateResult = await fetchDeviceEnergyStatistics(
					candidate,
					nextControls,
					nextPeriodId,
				);
				if (candidateResult?.chartData?.series?.some((series) => series.data?.length)) {
					result = candidateResult;
					resultDevice = candidate;
					break;
				}
			}
			if (current !== token) return;
			chartData = result?.chartData ?? null;
			displayedDevice = resultDevice;
			chartStatistic = resolveStatistic(chartData, resultDevice, result?.statistic);
		} catch {
			if (current === token) {
				chartData = null;
				chartStatistic = null;
				loadError = true;
			}
		} finally {
			if (current === token) loading = false;
		}
	};

	const refresh = () => load(device, controls, periodId, useDemoData, fallbackDeviceIdsKey);

	$: load(device, controls, periodId, useDemoData, fallbackDeviceIdsKey);
</script>

<EmWidgetShell widgetClass="energy-widget-chart energy-widget-device-trend">
	{#if loading}
		<div class="device-trend-state"><Loader size="m" /></div>
	{:else if loadError}
		<EmWidgetState
			kind="error"
			minHeight="220px"
			message={$format('energy_monitoring_load_error', {
				default: 'Energy data could not be loaded. Please try again.',
			})}
			onRefresh={refresh}
		/>
	{:else if displayChartData}
		<div class="device-trend-chart-block">
			<div
				class="device-trend-chart-card layer-chart-theme lc-card lc-color-override lc-card--accent-surface"
				style={`--lc-card-accent-color:${accentColor}`}
			>
				{#if chartStatistic?.available && chartStatistic.totalConsumed}
					<div class="device-trend-stats" aria-live="polite">
						<Text styleType="h2" class="text-600">{chartStatistic.totalConsumed}</Text>
						<Text styleType="miniature" class="text-color-muted">
							{$format('chart_period_total', { default: 'Period total' })}
						</Text>
						{#if statsDetail}
							<Text
								styleType="miniature"
								class="text-color-muted device-trend-stats-detail"
							>
								{statsDetail}
							</Text>
						{/if}
					</div>
				{/if}
				<EmLayerChartFrame height={CHART_FRAME_HEIGHT} reserveLegendSlot={false}>
					{#key displayChartData.id ?? displayChartData.name}
						<LayerChartRenderer
							chartData={displayChartData}
							legend={false}
							disableOuterSpacing
							colorSchema="blue"
							colorScheme={DEVICE_TREND_COLOR_SCHEME}
						/>
					{/key}
				</EmLayerChartFrame>
				{#if legendItems.length}
					<EmChartLegend class="device-trend-legend" items={legendItems} />
				{/if}
			</div>
		</div>
	{:else if !hasPowerMeterDevices($devices)}
		<EmWidgetState
			kind="setup"
			minHeight="220px"
			message={$format('energy_monitoring_state_device_meter_setup', {
				default: 'Add a power meter to see device energy.',
			})}
			actionHref={ENERGY_ADD_DEVICE_HREF}
			actionLabel={$format('button_add_device', { default: 'Add device' })}
		/>
	{:else if !deviceId}
		<EmWidgetState
			minHeight="220px"
			message={$format('energy_monitoring_state_device_no_activity', {
				default: 'No meter is using power right now.',
			})}
		/>
	{:else}
		<EmWidgetState
			minHeight="220px"
			message={$format('energy_monitoring_state_no_activity_message', {
				default: 'No activity to display.',
			})}
		/>
	{/if}
</EmWidgetShell>

<style>
	.device-trend-state {
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 220px;
		height: 100%;
	}

	.device-trend-chart-block {
		display: flex;
		flex-direction: column;
		flex: 1 1 auto;
		min-width: 0;
	}

	.device-trend-chart-card {
		display: flex;
		flex-direction: column;
		width: 100%;
		height: 100%;
		min-width: 0;
		border-radius: var(--radius-extra-big);
		overflow: hidden;
	}

	/*
	 * The design-system dashboard gives every generic chart widget a 360px
	 * minimum height. Device trends have their own compact chart frame, so that
	 * minimum only creates empty space above and below the content.
	 */
	:global(.container.energy-widget-device-trend) {
		height: auto;
		min-height: 0;
	}

	.device-trend-stats {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 2px;
		padding: var(--spacing-s) var(--spacing-s) 0;
		min-width: 0;
	}

	:global(.device-trend-stats-detail) {
		line-height: 1.3;
	}

	.device-trend-chart-card :global(.em-chart-frame .layer-chart-theme .lc-area-chart) {
		padding: 0;
	}

	.device-trend-chart-card :global(.layer-chart-theme--dashboard .lc-area-chart--dashboard) {
		min-height: var(--em-chart-height, 196px);
	}

	.device-trend-chart-card :global(.lc-group) {
		--lc-series-color: #248def;
		color: #248def;
		fill: #248def;
	}

	.device-trend-chart-card :global(.device-trend-legend.em-chart-legend-row) {
		margin-top: var(--spacing-m);
		padding: 0 var(--spacing-s) var(--spacing-s);
	}
</style>
